import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';

import {
    CalculatorStepAccordion,
    MessagePropType,
    PROVINCE_OPTIONS,
    Question,
    useForm,
    FormComputed,
} from '@ratehub/base-ui';
import { RATE_TYPES } from '@ratehub/documents/mtg-journey-document/stable';
import {
    getTermOptionsByRateType,
    createTermOptionResolver,
    GREATER_THAN_OR_EQUAL_TO_ZERO_VALIDATOR,
    RATE_OVER_20_PERCENT_VALIDATOR,
    START_DATE_IN_FUTURE_VALIDATOR,
    TERM_ENDED_VALIDATOR,

    MORTGAGE_PLACEHOLDERS,
    RATE_TYPE_MESSAGES,
} from '@ratehub/mtg-common';

import { createProviderOptions } from '../definitions/PenaltyProviders';
import BreakagePenaltyComputed from './BreakagePenaltyComputed';


function PenaltyStepInputs({ stepNumber, onConfirmComplete, title, ctaText, ctaDataName, ...otherProps }) {
    const intl = useIntl();
    const form = useForm();

    return (
        <>
            <BreakagePenaltyComputed
                name="calculatedPenaltyFeeObject"
                getFormValues={getFormValuesForBreakagePenalty}
                isDetailed={true}
            />

            <FormComputed
                name="irdFactor"
                value={getIRDFactor}
            />

            <FormComputed
                name="penaltyFee"
                value={getPenaltyFee}
            />

            <CalculatorStepAccordion
                stepNumber={stepNumber}
                showBadge={true}
                onConfirmComplete={onConfirmComplete}
                hasCompleteButton

                title={title}

                ctaText={ctaText}
                ctaDataName={ctaDataName}

                value="penaltyFee"
                valueFormat="CAD0"

                {...otherProps}
            >
                <Question
                    field={{
                        name: 'province',
                        type: 'choice',
                        options: PROVINCE_OPTIONS,
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.penaltyCalculator.province"
                            defaultMessage="Which province are you located in?"
                        />
                    )}
                    type="dropdown"
                    isSideBySide={true}
                />

                <Question
                    field={{
                        name: 'currentBalance',
                        type: 'integer',
                        validators: [
                            GREATER_THAN_OR_EQUAL_TO_ZERO_VALIDATOR,
                        ],
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.penaltyCalculator.currentMortgageBalance"
                            defaultMessage="What is your current mortgage balance?"
                        />
                    )}
                    tooltipMessage={(
                        <FormattedMessage
                            id="mtg-calculators.penaltyCalculator.currentMortgageBalanceTooltip"
                            defaultMessage="The balance your current mortgage will be on the day you are planning to refinance."
                        />
                    )}
                    placeholder={intl.formatMessage(MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER)}
                    type="currency"
                    isSideBySide={true}
                />

                <Question
                    field={{
                        name: 'currentRate',
                        type: 'float',
                        validators: [
                            RATE_OVER_20_PERCENT_VALIDATOR,
                        ],
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.penaltyCalculator.currentRate"
                            defaultMessage="What is your current mortgage rate?"
                        />
                    )}
                    placeholder={intl.formatMessage(MORTGAGE_PLACEHOLDERS.PERCENT_PLACEHOLDER)}
                    type="percent"
                    isSideBySide={true}
                />

                <Question
                    field={{
                        name: 'provider',
                        type: 'choice',
                        options: createProviderOptions(intl),
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.penaltyCalculator.provider"
                            defaultMessage="Who is your mortgage provider?"
                        />
                    )}
                    type="dropdown"
                    isSideBySide={true}
                />

                <Question
                    field={{
                        name: 'rateType',
                        type: 'choice',
                        options: [
                            {
                                label: intl.formatMessage(RATE_TYPE_MESSAGES[RATE_TYPES.FIXED]),
                                value: RATE_TYPES.FIXED,
                            },
                            {
                                label: intl.formatMessage(RATE_TYPE_MESSAGES[RATE_TYPES.VARIABLE]),
                                value: RATE_TYPES.VARIABLE,
                            },
                        ],
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.penaltyCalculator.rateType"
                            defaultMessage="What type of rate is it?"
                        />
                    )}
                    isSideBySide={true}
                    flowDirection="row"
                />

                <Question
                    field={{
                        name: 'originalTerm',
                        type: 'choice',
                        options: getTermOptionsByRateType(form.values.rateType),
                        disabled: !form.values.rateType,
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.penaltyCalculator.originalTerm"
                            defaultMessage="What is the current term of your mortgage?"
                        />
                    )}
                    tooltipMessage={(
                        <FormattedMessage
                            id="mtg-calculators.penaltyCalculator.originalTermTooltip"
                            defaultMessage="Your mortgage term is the length of time you committed to your mortgage rate. For example, on a 5-year fixed mortgage rate, the term is 5 years."
                        />
                    )}
                    renderOptionLabel={createTermOptionResolver(intl)}
                    type="dropdown"
                    isSideBySide={true}
                />

                <Question
                    field={{
                        name: 'startDate',
                        type: 'date',
                        validators: [
                            TERM_ENDED_VALIDATOR,
                            START_DATE_IN_FUTURE_VALIDATOR,
                        ],
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.penaltyCalculator.startDate"
                            defaultMessage="When did your mortgage start? (MM/DD/YYYY)"
                        />
                    )}
                    isSideBySide={true}
                    isSeparateInputs={true}
                />
            </CalculatorStepAccordion>
        </>
    );
}

PenaltyStepInputs.propTypes = {
    stepNumber: PropTypes.number.isRequired,
    onConfirmComplete: PropTypes.func,

    title: MessagePropType.isRequired,

    ctaText: MessagePropType.isRequired,
    ctaDataName: PropTypes.string.isRequired,
};

PenaltyStepInputs.defaultProps = {
    onConfirmComplete: undefined,
};

function getPenaltyFee(values) {
    return values.calculatedPenaltyFeeObject?.penalty;
}

function getIRDFactor(values) {
    return values.calculatedPenaltyFeeObject?.ird?.differential;
}

function getFormValuesForBreakagePenalty(values) {
    return {
        principal: values.currentBalance,
        rate: values.currentRate,
        type: values.rateType,
        term: values.originalTerm,
        provider: values.provider,
        startDate: values.startDate,
        province: values.province,
    };
}

export default observer(PenaltyStepInputs);
